import {
	IconAlignLeft,
	IconBriefcase,
	IconCalendar,
	IconCalendarCheck,
	IconCalendarClock,
	IconClipboardText,
	IconClock,
	IconExternalLink,
	IconEye,
	IconNotes,
	IconNumber123,
	IconRobotFace,
	IconToggleRight,
} from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Popover, Space, Tooltip, Typography } from "antd";
import { format } from "date-fns";
import { useState } from "react";
import { useAgents } from "@/common/artemis-operations/agents";
import { useJobRuns } from "@/common/artemis-operations/jobs";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import { useProjects } from "@/common/artemis-operations/projects";
import type { ArtemisJob, ArtemisJobRun } from "@/common/model";
import { describeCron, toLabel } from "@/common/utils";
import { AssigneeSelectorDrawer } from "@/components/agents/assignee-selector-drawer";
import { CronPicker } from "@/components/cron-picker";
import { BaseContainer, CustomIcon, IdField, QueryGuard } from "../base";
import { BaseDetails, EditableClickable, EditableText } from "../base-details";
import { CustomList } from "../custom-list";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { TaskStatusBadge } from "../tasks/task-badges";
import { JobStatusBadge } from "./job-badges";

// ── Job details ──────────────────────────────────────────────────────────────

interface JobDetailsProps {
	job: ArtemisJob;
	onSaveName?: (v: string) => void;
	onSaveDescription?: (v: string) => void;
	onSaveInstructions?: (v: string) => void;
	onSaveAgent?: (agentId: string) => void;
	onSaveSchedule?: (schedule: string) => void;
}

export function JobDetails({
	job,
	onSaveName,
	onSaveDescription,
	onSaveInstructions,
	onSaveAgent,
	onSaveSchedule,
}: JobDetailsProps) {
	const { data: projects } = useProjects(job.workspaceId);
	const orgSlug = useRouteOrgSlug();
	const { data: agents } = useAgents(job.workspaceId);
	const [agentDrawerOpen, setAgentDrawerOpen] = useState(false);
	const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
	const [pendingSchedule, setPendingSchedule] = useState(job.schedule);

	const agent = agents?.find((a) => a?.id === job.agentId);

	return (
		<>
			<BaseDetails
				item={job}
				fields={[
					{
						key: "id",
						icon: IconNumber123,
						tooltip: "ID",
						render: (id) => <IdField id={id as string} showCopyButton />,
					},
					{
						key: "name",
						icon: IconClipboardText,
						tooltip: "Name",
						render: (v) => (
							<EditableText onChange={onSaveName}>{v as string}</EditableText>
						),
					},
					{
						key: "description",
						icon: IconAlignLeft,
						tooltip: "Description",
						render: (v) => (
							<EditableText
								style={{ width: "100%", maxWidth: 600 }}
								onChange={onSaveDescription}
							>
								{(v as string) || ""}
							</EditableText>
						),
					},
					{
						key: "projectId",
						icon: IconBriefcase,
						tooltip: "Project",
						render: (v) => (
							<IdField
								id={projects?.find((p) => p?.id === v)?.name}
								link={`/${orgSlug}/workspaces/${job.workspaceId}/projects/${v}`}
							/>
						),
					},
					{
						key: "agentId",
						icon: IconRobotFace,
						tooltip: "Agent",
						render: () =>
							onSaveAgent ? (
								<EditableClickable onClick={() => setAgentDrawerOpen(true)}>
									<IdField
										id={agent?.name ?? job.agentId}
										link={`/${orgSlug}/workspaces/${job.workspaceId}/agents/${job.agentId}`}
									/>
								</EditableClickable>
							) : (
								<IdField
									id={agent?.name ?? job.agentId}
									link={`/${orgSlug}/workspaces/${job.workspaceId}/agents/${job.agentId}`}
								/>
							),
					},
					{
						key: "status",
						icon: IconToggleRight,
						tooltip: "Status",
						render: (s) => <JobStatusBadge status={s} />,
					},
					{
						key: "schedule",
						icon: IconClock,
						tooltip: "Cron schedule",
						render: (v) =>
							onSaveSchedule ? (
								<Popover
									open={schedulePopoverOpen}
									onOpenChange={setSchedulePopoverOpen}
									trigger="click"
									content={
										<Space vertical style={{ width: 340 }}>
											<CronPicker
												value={pendingSchedule}
												onChange={setPendingSchedule}
											/>
											<Space>
												<Button
													type="primary"
													onClick={() => {
														onSaveSchedule(pendingSchedule);
														setSchedulePopoverOpen(false);
													}}
												>
													Save
												</Button>
												<Button
													onClick={() => {
														setPendingSchedule(job.schedule);
														setSchedulePopoverOpen(false);
													}}
												>
													Cancel
												</Button>
											</Space>
										</Space>
									}
								>
									<Space
										size="small"
										style={{ cursor: "pointer" }}
										onClick={() => {
											setPendingSchedule(job.schedule);
											setSchedulePopoverOpen(true);
										}}
									>
										<Typography.Text code>{v as string}</Typography.Text>
										<Typography.Text type="secondary" ellipsis>
											{describeCron(v as string)}
										</Typography.Text>
									</Space>
								</Popover>
							) : (
								<Tooltip title={v}>
									<Typography.Text>{describeCron(v as string)}</Typography.Text>
								</Tooltip>
							),
					},
					{
						key: "lastFiredAt",
						icon: IconCalendarCheck,
						tooltip: "Last fired",
						render: (v) =>
							v ? format(new Date(v as string), "MM/dd/yyyy h:mm a") : "-",
					},
					{
						key: "nextFireAt",
						icon: IconCalendarClock,
						tooltip: "Next fire",
						render: (v) =>
							v ? format(new Date(v as string), "MM/dd/yyyy h:mm a") : "-",
					},
					{
						key: "instructions",
						icon: IconNotes,
						tooltip: "Instructions",
						render: (v) => (
							<EditableText
								paragraph
								style={{ whiteSpace: "pre-wrap", margin: 0, maxWidth: 600 }}
								onChange={onSaveInstructions}
								editableExtra={{ autoSize: { minRows: 3, maxRows: 12 } }}
							>
								{toLabel(v)}
							</EditableText>
						),
					},
					{
						key: "createdAt",
						icon: IconCalendar,
						tooltip: "Created",
						render: (v) => format(new Date(v as string), "MM/dd/yyyy h:mm a"),
					},
				]}
			/>
			{onSaveAgent && (
				<AssigneeSelectorDrawer
					open={agentDrawerOpen}
					workspaceId={job.workspaceId}
					assigneeId={job.agentId}
					assigneeType="AGENT"
					onClose={() => setAgentDrawerOpen(false)}
					onConfirm={(id) => {
						setAgentDrawerOpen(false);
						onSaveAgent(id);
					}}
				/>
			)}
		</>
	);
}

// ── Drawer ───────────────────────────────────────────────────────────────────

interface JobDetailsDrawerProps {
	job?: ArtemisJob;
	open: boolean;
	onClose: () => void;
	workspaceId?: string;
}

export function JobDetailsDrawer({
	job,
	open,
	onClose,
	workspaceId,
}: JobDetailsDrawerProps) {
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const runs = useJobRuns(job?.id as string);

	if (!job) return null;

	const jobPath = `/${orgSlug}/workspaces/${workspaceId ?? job.workspaceId}/jobs/${job.id}`;

	return (
		<CustomDrawer
			title={job.name}
			open={open}
			onClose={onClose}
			extra={
				<Space.Compact>
					<Button
						type="text"
						icon={<CustomIcon icon={IconEye} tooltip="View" />}
						onClick={() => navigate({ to: jobPath })}
					/>
					<Button
						type="text"
						icon={
							<CustomIcon
								icon={IconExternalLink}
								tooltip="Open in new window"
							/>
						}
						onClick={() => window.open(jobPath, "_blank", "noopener")}
					/>
				</Space.Compact>
			}
		>
			<Space vertical style={{ width: "100%" }} size="large">
				<JobDetails job={job} />
				<BaseContainer size="small" title="Recent runs">
					<QueryGuard isPending={runs.loading} isError={runs.isError}>
						<CustomList<ArtemisJobRun>
							data={runs.rows.slice(0, 5)}
							renderItem={(r) => (
								<Space vertical>
									<IdField key={r.taskId} id={r.taskId || ""} />
									<Typography.Text type="secondary">
										{format(new Date(r.firedAt as string), "MM/dd/yyyy h:mm a")}
									</Typography.Text>
									{r.task ? <TaskStatusBadge status={r.task.status} /> : null}
								</Space>
							)}
						/>
					</QueryGuard>
				</BaseContainer>
			</Space>
		</CustomDrawer>
	);
}
